/**
 * 全天球 (360度) 画像 = 正距円筒図法 (equirectangular) の座標変換 (pure 関数のみ、docs/27 §4)。
 *
 * ブラウザ側のパノラマビューア (`@citizen-walk/shared-ui` の `PanoramaViewer`、WebGL シェーダ) と
 * サーバ側の透視投影切り出し (`functions/src/ai/equirectProjection.ts`、sharp) が **同じ規約** で
 * 方位・仰角 ↔ 画像座標を対応づけるための正本。両者がズレると、AI 解析で得た `box_2d` (切り出し
 * 画像上の矩形) をパノラマ上の方位・仰角に戻したときにピンの位置が狂うため、ここ以外に計算式を
 * 持たない。
 *
 * 規約:
 *   - 画像の横位置 u∈[0,1) (左→右) は方位 `baseHeading + (u − 0.5) × 360°` に対応する。
 *     つまり **画像の中央列が baseHeading の方角** を向く (XMP GPano `PoseHeadingDegrees` の定義と同じ)。
 *   - 画像の縦位置 v∈[0,1] (上→下) は仰角 `(0.5 − v) × 180°` に対応する (上端 +90°、下端 −90°)。
 *   - 方位は北 0°、時計回り (東 90°)。ワールド座標は X=東 / Y=上 / Z=北。
 *   - 透視カメラは方位 heading・仰角 pitch を向き、水平画角 hfov。縦画角は
 *     `tan(vfov/2) = tan(hfov/2) × (height/width)` (既存 `computeVerticalFovDeg` と同じ透視関係)。
 */
const DEG = Math.PI / 180;
/** 方位 (deg、北0・時計回り) と仰角 (deg、上が正) から単位方向ベクトルを作る。 */
export function directionFromAzEl(azimuthDeg, elevationDeg) {
    const az = azimuthDeg * DEG;
    const el = elevationDeg * DEG;
    const c = Math.cos(el);
    return { x: c * Math.sin(az), y: Math.sin(el), z: c * Math.cos(az) };
}
/** 方向ベクトルから方位 (0〜360) と仰角 (−90〜90) を求める (`directionFromAzEl` の逆)。 */
export function azElFromDirection(d) {
    const len = Math.hypot(d.x, d.y, d.z) || 1;
    const y = Math.max(-1, Math.min(1, d.y / len));
    const az = (Math.atan2(d.x, d.z) / DEG + 360) % 360;
    return { azimuthDeg: az, elevationDeg: Math.asin(y) / DEG };
}
/**
 * 方位・仰角 → 正距円筒画像上の正規化座標 (u: 0〜1 左→右、v: 0〜1 上→下)。
 * `baseHeadingDeg` は画像中央列の方位。
 */
export function equirectUvFromAzEl(azimuthDeg, elevationDeg, baseHeadingDeg) {
    const rel = (azimuthDeg - baseHeadingDeg) / 360 + 0.5;
    const u = rel - Math.floor(rel);
    const v = Math.min(1, Math.max(0, 0.5 - elevationDeg / 180));
    return { u, v };
}
/** 正距円筒画像上の正規化座標 → 方位・仰角 (`equirectUvFromAzEl` の逆)。 */
export function azElFromEquirectUv(u, v, baseHeadingDeg) {
    const azimuthDeg = (((baseHeadingDeg + (u - 0.5) * 360) % 360) + 360) % 360;
    return { azimuthDeg, elevationDeg: (0.5 - v) * 180 };
}
/**
 * 透視画像上の正規化座標 (sx: 0〜1 左→右、sy: 0〜1 上→下) が指す世界方向ベクトル。
 * ピクセル中心を渡す場合は `(px + 0.5) / width` のように半ピクセル補正して呼ぶ。
 */
export function perspectiveRayDirection(sx, sy, cam) {
    const tanHalfH = Math.tan((cam.hfovDeg / 2) * DEG);
    const tanHalfV = tanHalfH / cam.aspect;
    // カメラ座標: +Z 前方, +X 右, +Y 上。
    let x = (sx * 2 - 1) * tanHalfH;
    let y = (1 - sy * 2) * tanHalfV;
    let z = 1;
    // pitch (X 軸回り、前方が上を向く方向が正)。
    const cp = Math.cos(cam.pitchDeg * DEG);
    const sp = Math.sin(cam.pitchDeg * DEG);
    const y1 = y * cp + z * sp;
    const z1 = -y * sp + z * cp;
    y = y1;
    z = z1;
    // heading (Y 軸回り、北→東が正 = 時計回り)。
    const ch = Math.cos(cam.headingDeg * DEG);
    const sh = Math.sin(cam.headingDeg * DEG);
    const x2 = x * ch + z * sh;
    const z2 = -x * sh + z * ch;
    x = x2;
    z = z2;
    const len = Math.hypot(x, y, z) || 1;
    return { x: x / len, y: y / len, z: z / len };
}
/**
 * 透視画像上の正規化座標 → 正距円筒画像上の正規化座標 (サーバ側切り出し / シェーダの参照実装)。
 */
export function equirectUvFromPerspective(sx, sy, cam, baseHeadingDeg) {
    const d = perspectiveRayDirection(sx, sy, cam);
    const { azimuthDeg, elevationDeg } = azElFromDirection(d);
    return equirectUvFromAzEl(azimuthDeg, elevationDeg, baseHeadingDeg);
}
/**
 * 透視画像 (hfov, aspect) の中の矩形 box2d ([ymin, xmin, ymax, xmax]、0〜1000 スケール、
 * `StreetViewPin.box2d` と同形) の中心が指す方位・仰角を、tan ベースの透視投影で厳密に求める。
 * 既存 `computeMarkerAzimuth` (線形近似) と中央・両端で一致し、中間で最大約4°改善する。
 */
export function azElFromPerspectiveBox(box2d, cam) {
    const [ymin, xmin, ymax, xmax] = box2d;
    const sx = (xmin + xmax) / 2 / 1000;
    const sy = (ymin + ymax) / 2 / 1000;
    return azElFromDirection(perspectiveRayDirection(sx, sy, cam));
}
/** 全天球 4 方向切り出しの既定 (Street View 静止画と同じ 640x480 / fov 90 / pitch 0)。 */
export const EQUIRECT_CROP_DEFAULTS = {
    width: 640,
    height: 480,
    hfovDeg: 90,
    pitchDeg: 0,
    /** baseHeading からの相対方位 (前方 / 右 / 後方 / 左)。 */
    headingOffsetsDeg: [0, 90, 180, 270],
};
/**
 * 世界の方位・仰角 → 透視カメラ画像上の正規化座標 (`perspectiveRayDirection` の逆変換、厳密)。
 * 3D パノラマ上にピンを重畳する AR オーバーレイ (`PanoramaViewer` の renderOverlay) で使う。
 * 既存 `projectToScreen` (admin、相対 pitch の近似) と異なりカメラ pitch≠0 でも厳密。
 */
export function projectAzElToPerspective(azimuthDeg, elevationDeg, cam) {
    const d = directionFromAzEl(azimuthDeg, elevationDeg);
    // heading の逆回転 (Y 軸回り、−heading)。
    const ch = Math.cos(-cam.headingDeg * DEG);
    const sh = Math.sin(-cam.headingDeg * DEG);
    const x = d.x * ch + d.z * sh;
    let y = d.y;
    let z = -d.x * sh + d.z * ch;
    // pitch の逆回転 (X 軸回り、−pitch)。
    const cp = Math.cos(-cam.pitchDeg * DEG);
    const sp = Math.sin(-cam.pitchDeg * DEG);
    const y1 = y * cp + z * sp;
    const z1 = -y * sp + z * cp;
    y = y1;
    z = z1;
    const tanHalfH = Math.tan((cam.hfovDeg / 2) * DEG);
    const tanHalfV = tanHalfH / cam.aspect;
    if (z <= 1e-6) {
        return { sx: x >= 0 ? 2 : -1, sy: 0.5, visible: false, inFront: false };
    }
    const sx = (x / z / tanHalfH + 1) / 2;
    const sy = (1 - y / z / tanHalfV) / 2;
    const visible = sx >= 0 && sx <= 1 && sy >= 0 && sy <= 1;
    return { sx, sy, visible, inFront: true };
}
/**
 * 多角形 (u は連続化済みで 0〜1 をはみ出しうる) を u∈[lo, hi] の帯で切り取る
 * (Sutherland–Hodgman、u 方向の 2 辺だけ)。
 */
function clipPolygonU(points, lo, hi) {
    const clipEdge = (pts, inside, uEdge) => {
        const out = [];
        for (let i = 0; i < pts.length; i++) {
            const cur = pts[i];
            const prev = pts[(i + pts.length - 1) % pts.length];
            const curIn = inside(cur);
            const prevIn = inside(prev);
            if (curIn !== prevIn) {
                const t = (uEdge - prev[0]) / (cur[0] - prev[0]);
                out.push([uEdge, prev[1] + (cur[1] - prev[1]) * t]);
            }
            if (curIn)
                out.push(cur);
        }
        return out;
    };
    const a = clipEdge(points, (p) => p[0] >= lo, lo);
    return clipEdge(a, (p) => p[0] <= hi, hi);
}
/**
 * 透視画像上の矩形 → 正距円筒画像上の多角形 (u/v 0〜1) の一覧 (docs/28 §7-3)。
 *
 * 写真のぼかし (顔・ナンバーの範囲) を、通常画角に分解した画像の検出結果から全天球画像へ戻すのに
 * 使う。矩形の 4 辺を `samplesPerEdge` 点ずつ透視→全天球に写すと、正距円筒上では曲がった多角形に
 * なる。左右の端 (u=0/1) をまたぐものは 2 つに分け、天頂・天底を含む矩形 (辺の写像が方位を一周
 * する) は極までの帯として閉じる。各多角形の u/v は 0〜1 に収まる。
 */
export function equirectPolygonsFromPerspectiveRect(rect, cam, baseHeadingDeg, samplesPerEdge = 8) {
    const { x0, y0, x1, y1 } = rect;
    const n = Math.max(1, Math.floor(samplesPerEdge));
    const perimeter = [];
    for (let i = 0; i < n; i++)
        perimeter.push([x0 + ((x1 - x0) * i) / n, y0]);
    for (let i = 0; i < n; i++)
        perimeter.push([x1, y0 + ((y1 - y0) * i) / n]);
    for (let i = 0; i < n; i++)
        perimeter.push([x1 - ((x1 - x0) * i) / n, y1]);
    for (let i = 0; i < n; i++)
        perimeter.push([x0, y1 - ((y1 - y0) * i) / n]);
    // u を連続化 (隣の点との差が半周を超えたら 1 周ずらす)。
    const poly = [];
    let prevU = null;
    for (const [sx, sy] of perimeter) {
        const uv = equirectUvFromPerspective(sx, sy, cam, baseHeadingDeg);
        let u = uv.u;
        const v = uv.v;
        if (prevU !== null) {
            while (u - prevU > 0.5)
                u -= 1;
            while (u - prevU < -0.5)
                u += 1;
        }
        prevU = u;
        poly.push([u, v]);
    }
    const first = poly[0];
    const last = poly[poly.length - 1];
    let closing = first[0] - last[0];
    while (closing > 0.5)
        closing -= 1;
    while (closing < -0.5)
        closing += 1;
    const winding = last[0] + closing - first[0];
    if (Math.abs(winding) > 0.5) {
        // 方位を一周した = 極を含む。カメラが向いている側の極まで帯を閉じる。
        const vPole = cam.pitchDeg < 0 ? 1 : 0;
        const endU = first[0] + winding;
        poly.push([endU, first[1]], [endU, vPole], [first[0], vPole]);
    }
    const out = [];
    const us = poly.map((p) => p[0]);
    const minU = Math.min(...us);
    const maxU = Math.max(...us);
    for (let shift = Math.floor(minU); shift <= Math.floor(maxU); shift++) {
        const shifted = poly.map(([u, v]) => [u - shift, v]);
        const clipped = clipPolygonU(shifted, 0, 1);
        if (clipped.length >= 3)
            out.push(clipped);
    }
    return out;
}
//# sourceMappingURL=equirectMath.js.map